import {
  createContext,
  type ReactNode,
  useContext,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
} from "react";
import { Bloom } from "./bloom.js";

/**
 * Arriving in a workspace — a reload, a sign-in, a switch.
 *
 * A blank page with the mark assembling in the middle, looping for as long as
 * anything holds it. When the last hold lets go, the loop is caught where it
 * is, the petals settle, and the mark turns home as it flies into the logo
 * marked `[data-intro-target]` (the rail's, or setup's) while the page appears
 * beneath it. With no logo on the page — an error notice — it simply fades.
 *
 * Holds are declared where the waiting is, with `<IntroHold>`, so the intro
 * knows nothing about sessions or brands. An `arrival` hold starts the intro
 * again (a workspace switch is an arrival); a `page` hold only keeps a running
 * intro going until the first screen's code is in, and never starts one, so
 * moving between pages does not replay it.
 *
 * The first assemble always finishes, so a fast load still gets the whole
 * motion. Under reduced motion the mark stands still and there is no flight.
 */
const EASE = "cubic-bezier(0.22, 1, 0.36, 1)";
const FIRST_ASSEMBLE_MS = 720;
const FLIGHT_MS = 780;

type Phase = "loading" | "leaving" | "done";
type HoldKind = "arrival" | "page";
interface Frozen {
  readonly angle: number;
  readonly petals: readonly { readonly transform: string; readonly opacity: string }[];
}
interface Holds {
  readonly hold: (kind: HoldKind) => () => void;
}

const IntroContext = createContext<Holds | null>(null);
/** Whether the intro has finished and the page is the reader's to look at. */
const SettledContext = createContext(true);

/**
 * True once the intro has landed, so something that should open on arrival —
 * What's new — waits for the mark to reach the rail rather than opening
 * underneath it. True outside the provider: there is no intro to wait for.
 */
export function useIntroSettled(): boolean {
  return useContext(SettledContext);
}

function prefersReducedMotion(): boolean {
  // jsdom has no matchMedia; a browser always does.
  return (
    typeof window.matchMedia === "function" &&
    window.matchMedia("(prefers-reduced-motion: reduce)").matches
  );
}

/** The rotation a computed `matrix(a, b, …)` carries, in degrees, 0–360. */
function angleOf(transform: string): number {
  const parts = /matrix\(([^,]+),\s*([^,]+)/.exec(transform);
  if (parts === null) return 0;
  const degrees = (Math.atan2(Number(parts[2]), Number(parts[1])) * 180) / Math.PI;
  return (degrees + 360) % 360;
}

function freeze(svg: SVGSVGElement): Frozen {
  return {
    angle: angleOf(getComputedStyle(svg).transform),
    petals: [...svg.querySelectorAll(".bloom-petal")].map((petal) => {
      const style = getComputedStyle(petal);
      return { transform: style.transform, opacity: style.opacity };
    }),
  };
}

/** Keeps the intro on screen for as long as this is mounted. Renders nothing. */
export function IntroHold({ kind }: { readonly kind: HoldKind }) {
  const intro = useContext(IntroContext);
  useEffect(() => intro?.hold(kind), [intro, kind]);
  return null;
}

export function IntroProvider({ children }: { readonly children: ReactNode }) {
  const [phase, setPhase] = useState<Phase>("loading");
  const [holds, setHolds] = useState(0);
  const started = useRef(performance.now());
  const frozen = useRef<Frozen | null>(null);
  // The hold callback is made once; it reads the phase through this.
  const phaseRef = useRef(phase);
  useEffect(() => {
    phaseRef.current = phase;
  }, [phase]);
  const overlay = useRef<HTMLDivElement>(null);
  const holder = useRef<HTMLDivElement>(null);
  const mark = useRef<SVGSVGElement>(null);

  const [api] = useState<Holds>(() => ({
    hold: (kind) => {
      setHolds((count) => count + 1);
      if (kind === "arrival" && phaseRef.current === "done") {
        started.current = performance.now();
        setPhase("loading");
      }
      return () => {
        setHolds((count) => count - 1);
      };
    },
  }));

  // Nothing holding it: let the first assemble land, then catch the loop.
  useEffect(() => {
    if (phase !== "loading" || holds > 0) return undefined;
    const wait = Math.max(0, FIRST_ASSEMBLE_MS - (performance.now() - started.current));
    const timer = window.setTimeout(() => {
      frozen.current = mark.current === null ? null : freeze(mark.current);
      setPhase("leaving");
    }, wait);
    return () => {
      window.clearTimeout(timer);
    };
  }, [phase, holds]);

  // Before paint, so the frozen frame and the first frame of the flight are one.
  useLayoutEffect(() => {
    if (phase !== "leaving") return;
    const svg = mark.current;
    const box = holder.current;
    const cover = overlay.current;
    const caught = frozen.current;
    // No Web Animations (jsdom), or reduced motion: no flight, just gone.
    if (svg === null || box === null || cover === null || caught === null) {
      setPhase("done");
      return;
    }
    if (typeof cover.animate !== "function" || prefersReducedMotion()) {
      setPhase("done");
      return;
    }
    const target = document.querySelector("[data-intro-target]");
    target?.classList.add("is-intro-hidden");

    [...svg.querySelectorAll(".bloom-petal")].forEach((petal, index) => {
      const from = caught.petals[index];
      if (from === undefined) return;
      petal.animate(
        [
          { transform: from.transform, opacity: from.opacity },
          { transform: "none", opacity: getComputedStyle(petal).opacity },
        ],
        { duration: 420, easing: EASE },
      );
    });

    if (target !== null) {
      const first = box.getBoundingClientRect();
      const last = target.getBoundingClientRect();
      const travel = `translate(${String(last.left - first.left)}px, ${String(last.top - first.top)}px) scale(${String(last.width / first.width)})`;
      svg.animate(
        [
          { transform: `rotate(${String(caught.angle)}deg)` },
          { transform: "rotate(360deg)" },
        ],
        { duration: FLIGHT_MS, easing: EASE, fill: "forwards" },
      );
      box.animate([{ transform: "none" }, { transform: travel }], {
        duration: FLIGHT_MS,
        easing: EASE,
        fill: "forwards",
      });
    }
    const page = getComputedStyle(cover).backgroundColor;
    const fade = cover.animate(
      [
        { backgroundColor: page, opacity: 1 },
        // Without a logo to land in, the mark leaves with the cover.
        { backgroundColor: "rgb(0 0 0 / 0)", opacity: target === null ? 0 : 1 },
      ],
      { duration: 520, delay: target === null ? 0 : 260, easing: EASE, fill: "forwards" },
    );
    void fade.finished.then(() => {
      target?.classList.remove("is-intro-hidden");
      setPhase("done");
    });
  }, [phase]);

  const still = prefersReducedMotion();
  return (
    <IntroContext.Provider value={api}>
      <SettledContext.Provider value={phase === "done"}>
        {children}
      </SettledContext.Provider>
      {phase === "done" ? null : (
        <div
          className="app-intro"
          ref={overlay}
          role="status"
          aria-label="Opening your workspace"
        >
          <div className="app-intro-mark" ref={holder}>
            <Bloom
              size={104}
              motion={phase === "loading" && !still ? "assemble" : "still"}
              svgRef={mark}
            />
          </div>
        </div>
      )}
    </IntroContext.Provider>
  );
}
